import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Copy, FileJson, Languages, Plus, RefreshCw, Save, Search, Settings, Sparkles, Trash2 } from 'lucide-react'
import {
  cloneExtractionProfile,
  cloneRulePack,
  createComplianceRule,
  createExtractionProfile,
  createRulePack,
  deleteComplianceRule,
  fetchExtractionProfiles,
  fetchRulePackTranslations,
  fetchRulePackWithRules,
  fetchRulePacks,
  translateRulePack,
  updateComplianceRule,
  updateExtractionProfileStatus,
  updateRulePackStatus,
  type ComplianceRuleDto,
  type ExtractionProfileDto,
  type RulePackDto,
} from '../services/api'
import { useAuth } from '../context/AuthContext'
import { EmptyState, FilterPill, KpiCard, MimCard, MimPage, StatusPill } from '../components/MimUi'
import { MIM_LAUNCH_LANGUAGE_CODES } from '../utils/mimLanguages'

type ComplianceRuleAction = 'CreateFinding' | 'NeedsReview' | 'BlockApproval'
type RuleFormState = {
  phrase: string
  actionIfFound: ComplianceRuleAction
  severity: ComplianceRuleDto['severity']
  key: string
  type: ComplianceRuleDto['type']
  parametersJson: string
  clauses: string
}

type RuleCreateRequest = Parameters<typeof createComplianceRule>[1]

// 'Custom' is deliberately excluded: the v2 comparison engine has no evaluator for it, so a rule
// of that type can never do anything except silently block every compliance run that uses it.
const categories: Array<'All' | ComplianceRuleDto['type']> = [
  'All',
  'RestrictedTerm',
  'UnsupportedClaim',
  'RequiredClaim',
  'BrokenLink',
  'FieldRule',
]

const EMPTY_RULE_FORM: RuleFormState = {
  phrase: '',
  actionIfFound: 'CreateFinding',
  severity: 'High',
  key: '',
  type: 'RestrictedTerm',
  parametersJson: '',
  clauses: '',
}

const complianceRuleActions: Array<{ value: ComplianceRuleAction; label: string; help: string }> = [
  { value: 'CreateFinding', label: 'Create finding', help: 'Open a compliance finding for review.' },
  { value: 'NeedsReview', label: 'Needs review', help: 'Flag the wording as requiring a manual decision.' },
  { value: 'BlockApproval', label: 'Block approval', help: 'Mark the issue as blocking until resolved.' },
]

export default function RulePacks() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [selectedPackId, setSelectedPackId] = useState('')
  const [category, setCategory] = useState<(typeof categories)[number]>('All')
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [packForm, setPackForm] = useState({ name: '', version: '1.0.0', description: '' })
  const [ruleForm, setRuleForm] = useState<RuleFormState>(EMPTY_RULE_FORM)
  const [singleRuleJson, setSingleRuleJson] = useState('')
  const [isImporting, setIsImporting] = useState(false)
  const [showClonePack, setShowClonePack] = useState(false)
  const [clonePackVersion, setClonePackVersion] = useState('')
  const [cloningProfileId, setCloningProfileId] = useState('')
  const [cloneProfileVersion, setCloneProfileVersion] = useState('')
  const packFileInputRef = useRef<HTMLInputElement>(null)

  const canManageRules = useMemo(() => {
    const businessLevel = (user?.businessLevel ?? '').toLowerCase()
    // Divisional / Group admins only (label map: Division / MasterAdmin)
    return businessLevel === 'masteradmin' || businessLevel === 'division'
  }, [user])

  const {
    data: packs = [],
    isLoading: packsLoading,
    isFetching: packsFetching,
  } = useQuery({ queryKey: ['rulepacks', 'rules-engine'], queryFn: fetchRulePacks })

  const {
    data: packWithRules,
    isLoading: rulesLoading,
    isFetching: rulesFetching,
  } = useQuery({
    queryKey: ['rulepack', selectedPackId, 'rules-engine'],
    queryFn: () => fetchRulePackWithRules(selectedPackId),
    enabled: !!selectedPackId,
  })

  const {
    data: extractionProfiles = [],
    isFetching: profilesFetching,
  } = useQuery({ queryKey: ['extraction-profiles', 'rules-engine'], queryFn: fetchExtractionProfiles })

  const { data: translationData, isFetching: translationsFetching } = useQuery({
    queryKey: ['rulepack-translations', selectedPackId, 'rules-engine'],
    queryFn: () => fetchRulePackTranslations(selectedPackId),
    enabled: !!selectedPackId,
  })

  const selectedPack = packWithRules?.pack ?? packs.find((pack) => pack.id === selectedPackId) ?? null
  const rules = packWithRules?.rules ?? []
  const visibleRules = useMemo(() => rules.filter((rule) => !isSystemComparisonRule(rule)), [rules])
  const canEditSelectedPack = selectedPack?.status === 'Draft'

  useEffect(() => {
    if (packs.length === 0) {
      if (selectedPackId) setSelectedPackId('')
      return
    }

    if (!selectedPackId || !packs.some((pack) => pack.id === selectedPackId)) {
      setSelectedPackId((packs.find((pack) => pack.status === 'Approved') ?? packs[0]).id)
    }
  }, [packs, selectedPackId])

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase()
    return visibleRules.filter((rule) => {
      const categoryMatch = category === 'All' || rule.type === category
      const textMatch =
        !text ||
        rule.name.toLowerCase().includes(text) ||
        rule.key.toLowerCase().includes(text) ||
        (rule.pattern || '').toLowerCase().includes(text) ||
        (rule.clauses || '').toLowerCase().includes(text)
      return categoryMatch && textMatch
    })
  }, [visibleRules, category, search])

  const createPackMutation = useMutation({
    mutationFn: createRulePack,
    onSuccess: async (created) => {
      setPackForm({ name: '', version: '1.0.0', description: '' })
      setSelectedPackId(created.id)
      setMessage('Draft rule pack created.')
      await qc.invalidateQueries({ queryKey: ['rulepacks', 'rules-engine'] })
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to create rule pack.')),
  })

  const addMutation = useMutation({
    mutationFn: () => createComplianceRule(selectedPackId, buildRuleRequest(ruleForm)),
    onSuccess: async () => {
      setRuleForm(EMPTY_RULE_FORM)
      setShowAdd(false)
      setMessage('Compliance rule added.')
      await invalidateRules()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to add compliance rule.')),
  })

  const statusMutation = useMutation({
    mutationFn: (status: RulePackDto['status']) => updateRulePackStatus(selectedPackId, status),
    onSuccess: async (_result, status) => {
      setMessage(status === 'Approved' ? 'Rule pack approved for compliance checks.' : 'Rule pack retired.')
      await invalidateRules()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to update rule pack status.')),
  })

  const createProfileMutation = useMutation({
    mutationFn: () => createExtractionProfile({
      name: 'Default extraction profile',
      version: `1.0.${extractionProfiles.length}`,
      fields: [{ canonicalFieldKey: 'product_name', displayName: 'Product name', requiredOnWebsite: true }],
    }),
    onSuccess: async () => {
      setMessage('Draft extraction profile created with a default "product_name" field.')
      await qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] })
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to create extraction profile.')),
  })

  const approveProfileMutation = useMutation({
    mutationFn: (id: string) => updateExtractionProfileStatus(id, 'Approved'),
    onSuccess: async () => {
      setMessage('Extraction profile approved. Compliance run preflight checks will now pass.')
      await qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] })
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to approve extraction profile.')),
  })

  const clonePackMutation = useMutation({
    mutationFn: () => cloneRulePack(selectedPackId, clonePackVersion.trim()),
    onSuccess: async (created) => {
      setShowClonePack(false)
      setClonePackVersion('')
      setMessage(`Draft copy "${created.version}" created with the same rules. Edit it, then approve when ready.`)
      setSelectedPackId(created.id)
      await qc.invalidateQueries({ queryKey: ['rulepacks', 'rules-engine'] })
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to clone rule pack.')),
  })

  const translateMutation = useMutation({
    mutationFn: () => translateRulePack(selectedPackId, [...MIM_LAUNCH_LANGUAGE_CODES]),
    onSuccess: async (result) => {
      setMessage(result.message)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['rulepacks', 'rules-engine'] }),
        qc.invalidateQueries({ queryKey: ['rulepack-translations', selectedPackId, 'rules-engine'] }),
      ])
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to build language packs.')),
  })

  const cloneProfileMutation = useMutation({
    mutationFn: (id: string) => cloneExtractionProfile(id, cloneProfileVersion.trim()),
    onSuccess: async (created) => {
      setCloningProfileId('')
      setCloneProfileVersion('')
      setMessage(`Draft copy "${created.version}" created with the same fields.`)
      await qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] })
      navigate(`/extraction-profiles/${created.id}`)
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to clone extraction profile.')),
  })

  const toggleMutation = useMutation({
    mutationFn: (rule: ComplianceRuleDto) => updateComplianceRule(rule.id, { enabled: !rule.enabled }),
    onSuccess: invalidateRules,
    onError: (err) => setError(getErrorMessage(err, 'Failed to update compliance rule.')),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteComplianceRule,
    onSuccess: async () => {
      setMessage('Compliance rule deleted.')
      await invalidateRules()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to delete compliance rule.')),
  })

  async function invalidateRules() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['rulepacks', 'rules-engine'] }),
      selectedPackId
        ? qc.invalidateQueries({ queryKey: ['rulepack', selectedPackId, 'rules-engine'] })
        : Promise.resolve(),
    ])
  }

  const clearFeedback = () => {
    setMessage('')
    setError('')
  }

  const handleCreatePack = () => {
    clearFeedback()
    if (!canManageRules) {
      setError('You need Master Data, Publisher, User Management, or Master Admin access to manage compliance rules.')
      return
    }
    if (!packForm.name.trim() || !packForm.version.trim()) {
      setError('Rule pack name and version are required.')
      return
    }

    createPackMutation.mutate({
      name: packForm.name.trim(),
      version: packForm.version.trim(),
      description: packForm.description.trim() || undefined,
    })
  }

  const handleAddRule = () => {
    clearFeedback()
    if (!selectedPackId || !selectedPack) {
      setError('Choose a rule pack first.')
      return
    }
    if (!canManageRules) {
      setError('You need Master Data, Publisher, User Management, or Master Admin access to manage compliance rules.')
      return
    }
    if (!canEditSelectedPack) {
      setError('Only draft rule packs can be edited.')
      return
    }
    if (!ruleForm.phrase.trim()) {
      setError('Word or phrase is required.')
      return
    }

    try {
      buildRuleRequest(ruleForm)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Parameters must be valid JSON.')
      return
    }

    addMutation.mutate()
  }

  const handleApplySingleRuleJson = () => {
    clearFeedback()
    try {
      const parsed = JSON.parse(singleRuleJson) as unknown
      const imported = parseRuleJson(parsed)
      const parameters = imported.parametersJson ? JSON.parse(imported.parametersJson) as Record<string, unknown> : {}
      const action = readAction(parameters)
      setRuleForm({
        phrase: imported.pattern || imported.name,
        actionIfFound: action,
        severity: imported.severity as ComplianceRuleDto['severity'],
        key: imported.key,
        type: imported.type as ComplianceRuleDto['type'],
        parametersJson: JSON.stringify(removeActionParameter(parameters)),
        clauses: imported.clauses || '',
      })
      setMessage('Rule JSON applied to the form. Review the fields, then select Save Rule.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The rule JSON is invalid.')
    }
  }

  const handlePackJsonFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    clearFeedback()
    if (!selectedPackId || !canEditSelectedPack || !canManageRules) {
      setError('Select a draft rule pack that you have permission to edit.')
      return
    }

    setIsImporting(true)
    try {
      const importedRules = parsePackJson(JSON.parse(await file.text()) as unknown)
      const existingKeys = new Set(rules.map((rule) => rule.key.toLowerCase()))
      let created = 0
      let skipped = 0
      const failures: string[] = []

      for (const rule of importedRules) {
        if (existingKeys.has(rule.key.toLowerCase())) {
          skipped++
          continue
        }
        try {
          await createComplianceRule(selectedPackId, rule)
          existingKeys.add(rule.key.toLowerCase())
          created++
        } catch (err) {
          failures.push(`${rule.key}: ${getErrorMessage(err, 'Import failed')}`)
        }
      }

      await invalidateRules()
      if (failures.length > 0) {
        setError(`Imported ${created}; skipped ${skipped}; failed ${failures.length}. ${failures.slice(0, 3).join(' | ')}`)
      } else {
        setMessage(`Pack import complete: ${created} rule${created === 1 ? '' : 's'} added and ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped.`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The pack JSON is invalid.')
    } finally {
      setIsImporting(false)
    }
  }

  const handleChangeStatus = (status: RulePackDto['status']) => {
    clearFeedback()
    if (!selectedPackId || !selectedPack) return
    if (!canManageRules) {
      setError('You need Master Data, Publisher, User Management, or Master Admin access to manage compliance rules.')
      return
    }
    statusMutation.mutate(status)
  }

  const handleToggleRule = (rule: ComplianceRuleDto) => {
    clearFeedback()
    if (!canManageRules || !canEditSelectedPack) {
      setError('Only draft rule packs can be edited.')
      return
    }
    toggleMutation.mutate(rule)
  }

  const handleDeleteRule = (rule: ComplianceRuleDto) => {
    clearFeedback()
    if (!canManageRules || !canEditSelectedPack) {
      setError('Only draft rule packs can be edited.')
      return
    }
    if (!confirm(`Delete "${rule.name}"?`)) return
    deleteMutation.mutate(rule.id)
  }

  const totalActive = visibleRules.filter((rule) => rule.enabled).length
  const criticalRules = visibleRules.filter((rule) => rule.enabled && ['Critical', 'High'].includes(rule.severity)).length
  const busy = createPackMutation.isPending || addMutation.isPending || statusMutation.isPending || toggleMutation.isPending || deleteMutation.isPending || isImporting

  return (
    <MimPage title="Rules Engine" subtitle="Manage banned terms and compliance rules">
      <div className="mim-stack">
        <div className="mim-grid-4">
          <KpiCard label="Total Rules" value={visibleRules.length} />
          <KpiCard label="Active Rules" value={totalActive} tone="green" />
          <KpiCard label="Critical Rules" value={criticalRules} tone="red" />
          <KpiCard label="Rule Packs" value={packs.length} tone="orange" />
        </div>

        {message && <div className="rules-feedback rules-feedback-success">{message}</div>}
        {error && <div className="rules-feedback rules-feedback-error">{error}</div>}

        <div className="mim-two-column">
          <div className="mim-stack">
            <MimCard title="Categories">
              <div className="rules-category-list">
                {categories.map((item) => {
                  const count = item === 'All' ? visibleRules.length : visibleRules.filter((rule) => rule.type === item).length
                  return (
                    <button key={item} type="button" className={category === item ? 'active' : ''} onClick={() => setCategory(item)}>
                      <span>{friendlyCategory(item)}</span>
                      <strong>{count}</strong>
                    </button>
                  )
                })}
              </div>
            </MimCard>

            <MimCard title="Rule Pack">
              {packsLoading && packs.length === 0 ? (
                <EmptyState title="Loading rule packs" />
              ) : packs.length === 0 ? (
                <div className="mim-note">No rule packs found. Create a draft pack below to start adding rules.</div>
              ) : (
                <div className="mim-stack">
                  <select className="mim-select" value={selectedPackId} onChange={(e) => { clearFeedback(); setSelectedPackId(e.target.value) }}>
                    {packs.map((pack) => (
                      <option key={pack.id} value={pack.id}>{pack.name} v{pack.version} ({pack.status})</option>
                    ))}
                  </select>

                  {selectedPack && (
                    <div className="rules-selected-pack">
                      <div>
                        <strong>{selectedPack.name}</strong>
                        <span>Version {selectedPack.version} · {((selectedPack as RulePackDto).languageCode || 'en').toUpperCase()}</span>
                      </div>
                      <StatusPill status={selectedPack.status} />
                    </div>
                  )}

                  <div className="mim-toolbar">
                    {selectedPack?.status === 'Draft' && (
                      <button type="button" className="mim-btn mim-btn-primary" disabled={!canManageRules || busy} onClick={() => handleChangeStatus('Approved')}>
                        <CheckCircle2 size={14} /> Approve
                      </button>
                    )}
                    {selectedPack && selectedPack.status !== 'Retired' && (
                      <button type="button" className="mim-btn mim-btn-secondary" disabled={!canManageRules || busy} onClick={() => handleChangeStatus('Retired')}>
                        Retire
                      </button>
                    )}
                    {selectedPack && (
                      <button
                        type="button"
                        className="mim-btn mim-btn-secondary"
                        disabled={!canManageRules || busy}
                        onClick={() => { clearFeedback(); setShowClonePack((current) => !current) }}
                      >
                        <Copy size={14} /> Clone as new draft
                      </button>
                    )}
                    <button type="button" className="mim-btn mim-btn-secondary" disabled={packsFetching || rulesFetching} onClick={invalidateRules}>
                      <RefreshCw size={14} /> Refresh
                    </button>
                  </div>

                  {showClonePack && selectedPack && (
                    <div className="mim-toolbar">
                      <input
                        className="mim-input"
                        placeholder="New version, e.g. 1.1.0"
                        value={clonePackVersion}
                        onChange={(e) => setClonePackVersion(e.target.value)}
                        style={{ maxWidth: 200 }}
                      />
                      <button
                        type="button"
                        className="mim-btn mim-btn-primary"
                        disabled={!clonePackVersion.trim() || clonePackMutation.isPending}
                        onClick={() => { clearFeedback(); clonePackMutation.mutate() }}
                      >
                        Create draft copy
                      </button>
                    </div>
                  )}

                  <div className="mim-note">
                    {selectedPack?.status === 'Draft'
                      ? 'Draft packs can be edited. Approve when the rules are ready for checks.'
                      : selectedPack?.status === 'Approved'
                        ? 'Approved packs are available to compliance checks and are read-only here. Clone to a new draft to revise the rules.'
                        : 'Retired packs are kept for history and are not used for new checks.'}
                  </div>
                </div>
              )}
            </MimCard>

            <MimCard title="Translations">
              {!selectedPackId ? (
                <div className="mim-note">Select a rule pack to manage its language variants.</div>
              ) : (
                <div className="mim-stack">
                  <div className="mim-note">
                    AI builds Draft copies for the seven MIM languages (FR, DE, NL, ES, IT, PL).
                    Review each pack, then Approve. Website Check uses the matching language, or English if a translation is not approved yet.
                  </div>
                  <button
                    type="button"
                    className="mim-btn mim-btn-primary"
                    disabled={!canManageRules || translateMutation.isPending}
                    onClick={() => { clearFeedback(); translateMutation.mutate() }}
                  >
                    <Languages size={14} /> {translateMutation.isPending ? 'Building language packs…' : 'Build MIM language packs (FR DE NL ES IT PL)'}
                  </button>
                  {translationsFetching && !translationData ? (
                    <div className="mim-note">Loading translations…</div>
                  ) : (translationData?.translations?.length ?? 0) === 0 ? (
                    <div className="mim-note">No language variants yet.</div>
                  ) : (
                    <div className="rules-category-list">
                      {(translationData?.translations ?? []).map((row) => (
                        <button
                          key={row.id}
                          type="button"
                          className={selectedPackId === row.id ? 'active' : ''}
                          onClick={() => { clearFeedback(); setSelectedPackId(row.id) }}
                          title={`Open ${row.name} v${row.version}`}
                        >
                          <span>
                            {(row.languageCode || 'en').toUpperCase()}
                            {row.baseRulePackId ? '' : ' (base)'} — {row.status}
                          </span>
                          <strong>{selectedPackId === row.id ? 'Open' : '›'}</strong>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </MimCard>

            <MimCard title="Extraction Profiles">
              <div className="mim-note" style={{ marginBottom: 8 }}>
                A compliance run needs at least one <strong>approved</strong> extraction profile and an
                approved PCR revision. A rule pack is optional - it only adds extra checks on top of
                core PCR field comparison.
              </div>
              {extractionProfiles.length === 0 ? (
                <div className="mim-note">No extraction profiles yet.</div>
              ) : (
                <div className="mim-stack" style={{ marginBottom: 8 }}>
                  {extractionProfiles.map((profile: ExtractionProfileDto) => (
                    <div key={profile.id} className="rules-selected-pack">
                      <div>
                        <strong>{profile.name}</strong>
                        <span>
                          v{profile.version} - {profile.fieldCount} field{profile.fieldCount === 1 ? '' : 's'}
                          {' · '}
                          {profile.businessEntityName ? `Scope: ${profile.businessEntityName}` : profile.divisionName ? `Scope: ${profile.divisionName}` : 'Scope: Global'}
                        </span>
                      </div>
                      <div className="mim-toolbar">
                        <StatusPill status={profile.status} />
                        {profile.status === 'Draft' && (
                          <button
                            type="button"
                            className="mim-btn mim-btn-primary"
                            disabled={!canManageRules || approveProfileMutation.isPending}
                            onClick={() => { clearFeedback(); approveProfileMutation.mutate(profile.id) }}
                          >
                            <CheckCircle2 size={14} /> Approve
                          </button>
                        )}
                        <button type="button" className="mim-btn mim-btn-secondary" onClick={() => navigate(`/extraction-profiles/${profile.id}`)}>
                          <Settings size={14} /> Manage fields
                        </button>
                        <button
                          type="button"
                          className="mim-btn mim-btn-secondary"
                          onClick={() => { clearFeedback(); setCloningProfileId((current) => current === profile.id ? '' : profile.id) }}
                        >
                          <Copy size={14} /> Clone
                        </button>
                      </div>
                      {cloningProfileId === profile.id && (
                        <div className="mim-toolbar" style={{ marginTop: 8 }}>
                          <input
                            className="mim-input"
                            placeholder="New version, e.g. 1.1.0"
                            value={cloneProfileVersion}
                            onChange={(e) => setCloneProfileVersion(e.target.value)}
                            style={{ maxWidth: 200 }}
                          />
                          <button
                            type="button"
                            className="mim-btn mim-btn-primary"
                            disabled={!cloneProfileVersion.trim() || cloneProfileMutation.isPending}
                            onClick={() => cloneProfileMutation.mutate(profile.id)}
                          >
                            Create draft copy
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mim-toolbar">
                <button
                  type="button"
                  className="mim-btn mim-btn-primary"
                  disabled={!canManageRules}
                  onClick={() => navigate('/extraction-profiles/generate')}
                >
                  <Sparkles size={14} /> Generate from PCR data
                </button>
                <button
                  type="button"
                  className="mim-btn mim-btn-secondary"
                  disabled={!canManageRules || createProfileMutation.isPending || profilesFetching}
                  onClick={() => { clearFeedback(); createProfileMutation.mutate() }}
                >
                  <Plus size={14} /> Quick-create draft profile
                </button>
              </div>
            </MimCard>

            <MimCard title="Create Draft Pack">
              {canManageRules ? (
                <div className="mim-stack">
                  <label>
                    <span className="mim-label">Name *</span>
                    <input className="mim-input" value={packForm.name} onChange={(e) => setPackForm((current) => ({ ...current, name: e.target.value }))} placeholder="MIM compliance rules" />
                  </label>
                  <div className="mim-grid-2">
                    <label>
                      <span className="mim-label">Version *</span>
                      <input className="mim-input" value={packForm.version} onChange={(e) => setPackForm((current) => ({ ...current, version: e.target.value }))} placeholder="1.0.0" />
                    </label>
                    <label>
                      <span className="mim-label">Description</span>
                      <input className="mim-input" value={packForm.description} onChange={(e) => setPackForm((current) => ({ ...current, description: e.target.value }))} placeholder="Optional" />
                    </label>
                  </div>
                  <button type="button" className="mim-btn mim-btn-primary" disabled={createPackMutation.isPending} onClick={handleCreatePack}>
                    <Plus size={14} /> Create Pack
                  </button>
                </div>
              ) : (
                <div className="mim-note">Compliance rules are read-only for your account.</div>
              )}
            </MimCard>
          </div>

          <div className="mim-stack">
            <div className="mim-toolbar">
              <div className="mim-search">
                <Search size={15} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search terms or descriptions..." />
              </div>
              <button
                type="button"
                className="mim-btn mim-btn-primary"
                disabled={!selectedPackId || !canManageRules || !canEditSelectedPack}
                title={!selectedPackId ? 'Choose a rule pack first' : !canEditSelectedPack ? 'Only draft rule packs can be edited' : undefined}
                onClick={() => { clearFeedback(); setShowAdd((current) => !current) }}
              >
                <Plus size={14} /> Add Rule
              </button>
              <input ref={packFileInputRef} type="file" accept="application/json,.json" hidden onChange={handlePackJsonFile} />
              <button
                type="button"
                className="mim-btn mim-btn-secondary"
                disabled={!selectedPackId || !canManageRules || !canEditSelectedPack || isImporting}
                title={!canEditSelectedPack ? 'Only draft rule packs can be edited' : 'Import all rules from a JSON pack'}
                onClick={() => packFileInputRef.current?.click()}
              >
                <FileJson size={14} /> {isImporting ? 'Importing…' : 'Import Pack JSON'}
              </button>
            </div>

            {showAdd && selectedPack && (
              <MimCard title="New Compliance Rule" className="rules-add-card">
                <label>
                  <span className="mim-label">Paste Single Rule JSON</span>
                  <textarea
                    className="mim-textarea"
                    rows={4}
                    value={singleRuleJson}
                    onChange={(e) => setSingleRuleJson(e.target.value)}
                    placeholder={'{"key":"restricted_term_sustainable","name":"Restricted Term: sustainable","type":"RestrictedTerm","pattern":"sustainable","severity":"High","clauses":"Clause 5"}'}
                  />
                  <span className="mim-help-text">Paste one rule, then apply it to populate the fields below.</span>
                </label>
                <div className="mim-toolbar" style={{ margin: '8px 0 16px' }}>
                  <button type="button" className="mim-btn mim-btn-secondary" disabled={!singleRuleJson.trim()} onClick={handleApplySingleRuleJson}>
                    <FileJson size={14} /> Apply JSON to Fields
                  </button>
                </div>
                <div className="mim-grid-2">
                  <label>
                    <span className="mim-label">Word or Phrase *</span>
                    <input className="mim-input" value={ruleForm.phrase} onChange={(e) => setRuleForm((current) => ({ ...current, phrase: e.target.value }))} placeholder="e.g. eco-friendly" />
                    <span className="mim-help-text">The exact word or phrase to look for in distributor content.</span>
                  </label>
                  <label>
                    <span className="mim-label">Action if Found</span>
                    <select className="mim-select" value={ruleForm.actionIfFound} onChange={(e) => setRuleForm((current) => ({ ...current, actionIfFound: e.target.value as ComplianceRuleAction }))}>
                      {complianceRuleActions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <span className="mim-help-text">{complianceRuleActions.find((option) => option.value === ruleForm.actionIfFound)?.help}</span>
                  </label>
                  <label>
                    <span className="mim-label">Category</span>
                    <select className="mim-select" value={ruleForm.type} onChange={(e) => setRuleForm((current) => ({ ...current, type: e.target.value as ComplianceRuleDto['type'] }))}>
                      {categories.filter((item) => item !== 'All').map((item) => <option key={item} value={item}>{friendlyCategory(item)}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="mim-label">Severity</span>
                    <select className="mim-select" value={ruleForm.severity} onChange={(e) => setRuleForm((current) => ({ ...current, severity: e.target.value as ComplianceRuleDto['severity'] }))}>
                      {['Critical', 'High', 'Medium', 'Low', 'Info'].map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="mim-label">Clause or Reference</span>
                    <input className="mim-input" value={ruleForm.clauses} onChange={(e) => setRuleForm((current) => ({ ...current, clauses: e.target.value }))} placeholder="MIM Clause 9" />
                  </label>
                  <label>
                    <span className="mim-label">Technical Key</span>
                    <input className="mim-input" value={ruleForm.key} onChange={(e) => setRuleForm((current) => ({ ...current, key: e.target.value }))} placeholder="Auto-generated if left blank" />
                  </label>
                </div>
                <label style={{ marginTop: 12 }}>
                  <span className="mim-label">Parameters JSON</span>
                  <input className="mim-input" value={ruleForm.parametersJson} onChange={(e) => setRuleForm((current) => ({ ...current, parametersJson: e.target.value }))} placeholder='Optional, e.g. {"threshold":3}' />
                </label>
                <div className="mim-toolbar" style={{ marginTop: 12 }}>
                  <button type="button" className="mim-btn mim-btn-primary" disabled={!ruleForm.phrase.trim() || addMutation.isPending} onClick={handleAddRule}>
                    <Save size={14} /> Save Rule
                  </button>
                  <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </MimCard>
            )}

            <MimCard>
              {!selectedPackId ? (
                <EmptyState title="No rule pack selected" detail="Create or select a draft rule pack to manage compliance rules." />
              ) : rulesLoading ? (
                <EmptyState title="Loading rules" />
              ) : visibleRules.length === 0 ? (
                <EmptyState title="No user-managed rules in this pack" detail={canEditSelectedPack ? 'Use Add Rule to create the first compliance rule.' : 'This pack only contains system rules or no rules yet.'} />
              ) : filtered.length === 0 ? (
                <EmptyState title="No rules match this filter" detail="Clear the search or choose another category." />
              ) : (
                <div className="mim-table-wrap">
                  <table className="mim-table">
                    <thead>
                      <tr>
                        <th>Term / Phrase</th>
                        <th>Category</th>
                        <th>Severity</th>
                        <th>Matches</th>
                        <th>Description</th>
                        <th>Action</th>
                        <th>Active</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((rule) => (
                        <tr key={rule.id} className={!rule.enabled ? 'rules-disabled-row' : ''}>
                          <td><span className="mim-mono">"{rule.pattern || rule.name}"</span></td>
                          <td><FilterPill active={false}>{friendlyCategory(rule.type)}</FilterPill></td>
                          <td><StatusPill status={rule.severity} /></td>
                          <td>0</td>
                          <td>{rule.clauses || rule.key || '-'}</td>
                          <td>{getRuleActionLabel(getRuleAction(rule))}</td>
                          <td>
                            <button
                              type="button"
                              className={`mim-switch ${rule.enabled ? 'on' : ''}`}
                              disabled={!canManageRules || !canEditSelectedPack || toggleMutation.isPending}
                              title={!canEditSelectedPack ? 'Only draft rule packs can be edited' : undefined}
                              onClick={() => handleToggleRule(rule)}
                            >
                              <span />
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="mim-icon-button"
                              disabled={!canManageRules || !canEditSelectedPack || deleteMutation.isPending}
                              title={!canEditSelectedPack ? 'Only draft rule packs can be edited' : 'Delete rule'}
                              onClick={() => handleDeleteRule(rule)}
                            >
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mim-table-footer">{filtered.length} rules shown, {totalActive} active</div>
                </div>
              )}
            </MimCard>
          </div>
        </div>
      </div>
    </MimPage>
  )
}

function normalizeRuleKey(phrase: string) {
  const normalized = phrase
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return `restricted_term_${normalized || Date.now()}`
}

function buildRuleRequest(form: RuleFormState) {
  const base = form.parametersJson.trim() ? JSON.parse(form.parametersJson) : {}
  if (base === null || Array.isArray(base) || typeof base !== 'object') {
    throw new Error('Advanced parameters must be a JSON object.')
  }

  const phrase = form.phrase.trim()
  return {
    key: form.key.trim() || normalizeRuleKey(phrase),
    name: `${friendlyCategory(form.type).replace(/s$/, '')}: ${phrase}`,
    type: form.type,
    pattern: phrase,
    parametersJson: JSON.stringify({ ...base, actionIfFound: form.actionIfFound }),
    severity: form.severity,
    clauses: form.clauses.trim() || undefined,
  }
}

function parsePackJson(value: unknown): RuleCreateRequest[] {
  const source = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.rules)
      ? value.rules
      : null
  if (!source) throw new Error('Pack JSON must contain a "rules" array (or be an array of rules).')
  if (source.length === 0) throw new Error('The JSON pack contains no rules.')
  return source.map((rule, index) => {
    try {
      return parseRuleJson(rule)
    } catch (err) {
      throw new Error(`Rule ${index + 1}: ${err instanceof Error ? err.message : 'Invalid rule.'}`)
    }
  })
}

function parseRuleJson(value: unknown): RuleCreateRequest {
  if (!isRecord(value)) throw new Error('Rule JSON must be an object.')

  const type = readString(value.type) || 'RestrictedTerm'
  const allowedTypes = categories.filter((item) => item !== 'All') as string[]
  if (!allowedTypes.includes(type)) throw new Error(`Unknown rule type "${type}".`)

  const severity = readString(value.severity) || 'High'
  if (!['Critical', 'High', 'Medium', 'Low', 'Info'].includes(severity)) {
    throw new Error(`Unknown severity "${severity}".`)
  }

  const pattern = readString(value.pattern) || readString(value.phrase) || readString(value.wordOrPhrase)
  const suppliedName = readString(value.name)
  const phraseFromName = suppliedName.includes(':') ? suppliedName.slice(suppliedName.indexOf(':') + 1).trim() : suppliedName
  const parameters = parseParameters(value.parametersJson ?? value.parameters)
  const checkFor = Array.isArray(parameters.check_for)
    ? parameters.check_for.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
  const phrase = pattern || phraseFromName || (type === 'RequiredClaim' && checkFor.length > 0 ? checkFor[0] : '')
  if (!phrase && type !== 'RequiredClaim') throw new Error('A pattern, phrase, wordOrPhrase, or name is required.')
  if (type === 'RequiredClaim' && !phrase && checkFor.length === 0) {
    throw new Error('RequiredClaim rules need a pattern/phrase or a check_for list in parametersJson.')
  }

  const key = readString(value.key) || normalizeRuleKey(phrase || readString(value.key) || 'required_claim')
  const topLevelAction = readString(value.actionIfFound)
  if (topLevelAction) parameters.actionIfFound = topLevelAction
  if (parameters.actionIfFound && !complianceRuleActions.some((option) => option.value === parameters.actionIfFound)) {
    throw new Error(`Unknown actionIfFound "${String(parameters.actionIfFound)}".`)
  }

  return {
    key,
    name: suppliedName || (type === 'RequiredClaim' && checkFor.length > 1
      ? 'Required Reference Check'
      : `${friendlyCategory(type).replace(/s$/, '')}: ${phrase}`),
    type,
    pattern: type === 'RequiredClaim' && checkFor.length > 0 && !pattern ? undefined : (pattern || phrase || undefined),
    parametersJson: Object.keys(parameters).length > 0 ? JSON.stringify(parameters) : undefined,
    severity,
    clauses: readString(value.clauses) || readString(value.clause) || undefined,
    enabled: typeof value.enabled === 'boolean' ? value.enabled : true,
  }
}

function parseParameters(value: unknown): Record<string, unknown> {
  if (value === undefined || value === null || value === '') return {}
  const parsed = typeof value === 'string' ? JSON.parse(value) as unknown : value
  if (!isRecord(parsed)) throw new Error('parametersJson must contain a JSON object.')
  return { ...parsed }
}

function readAction(parameters: Record<string, unknown>): ComplianceRuleAction {
  const action = parameters.actionIfFound
  return complianceRuleActions.some((option) => option.value === action)
    ? action as ComplianceRuleAction
    : 'CreateFinding'
}

function removeActionParameter(parameters: Record<string, unknown>) {
  const result = { ...parameters }
  delete result.actionIfFound
  return result
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function readString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function getRuleAction(rule: ComplianceRuleDto): ComplianceRuleAction {
  if (!rule.parametersJson?.trim()) return 'CreateFinding'
  try {
    const parameters = JSON.parse(rule.parametersJson)
    return typeof parameters.actionIfFound === 'string' ? parameters.actionIfFound : 'CreateFinding'
  } catch {
    return 'CreateFinding'
  }
}

function getRuleActionLabel(action: string | undefined) {
  return complianceRuleActions.find((option) => option.value === action)?.label ?? 'Create finding'
}

function isSystemComparisonRule(rule: ComplianceRuleDto) {
  return (
    rule.key === 'field_mismatch' ||
    rule.key === 'download_hash_mismatch' ||
    (rule.type === 'FieldRule' && rule.parametersJson?.includes('"systemRule":true'))
  )
}

function friendlyCategory(value: string) {
  if (value === 'RestrictedTerm') return 'Restricted Terms'
  if (value === 'UnsupportedClaim') return 'Unsubstantiated Claims'
  if (value === 'RequiredClaim') return 'Required References'
  if (value === 'BrokenLink') return 'Broken Links'
  if (value === 'FieldRule') return 'Field Rules'
  return value
}

function getErrorMessage(err: unknown, fallback: string) {
  const responseData = (err as { response?: { data?: unknown } })?.response?.data
  if (typeof responseData === 'string') return responseData
  if (responseData && typeof responseData === 'object' && 'message' in responseData) {
    const message = (responseData as { message?: unknown }).message
    if (typeof message === 'string') return message
  }

  const message = (err as { message?: unknown })?.message
  return typeof message === 'string' ? message : fallback
}
